import { i18n } from "@/utils/i18n";
import { Button } from "@aidergroup/ui-v2";
import { Box, CircularProgress, Divider, Typography, useTheme } from "@mui/material";
import React from "react";
import { useDownloadVerification, useRegistryExport } from "./hooks";
import { Report } from "@/types/report";
import { useNavigate, useParams } from "react-router-dom";
import RegistryExportDialog from "./registry-export-dialog";

interface Props {
    name: Report["name"];
    startDate: Report["startDate"];
    endDate: Report["endDate"];
    attachments: Report["attachments"];
    hasSectionMap: boolean;
}

// Mirrors the backend (registry-export.ts loadOriginalReportPdf): the generated
// report PDF is always named rapport_<id>.pdf, distinct from human-named user
// attachments and the signed re-upload (rapport_<id>_granskad.pdf).
const isReportPdf = (attachment: Report["attachments"][number]) =>
    /^rapport_\d+\.pdf$/i.test(attachment.name ?? "");

const Header: React.FC<Props> = ({ name, startDate, endDate, attachments, hasSectionMap }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { onDownload, downloading } = useDownloadVerification();
    const { downloading: exporting, downloadRegistryExport } = useRegistryExport();
    const [registryDialogOpen, setRegistryDialogOpen] = React.useState(false);
    const { oid, sid, rid } = useParams();

    const onNavigateToVerifications = () => {
        navigate(`/organizations/${oid}/submissions/${sid}/reports/${rid}/verifications`);
    };
    const onNavigateToCategoriesOverview = () => {
        navigate(`/organizations/${oid}/submissions/${sid}/reports/${rid}/categories-overview`);
    };

    return (
        <Box
            padding="20px 40px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            position="sticky"
            top="0"
            zIndex={99}
            sx={{ backgroundColor: theme.palette.grey[100] }}
        >
            <Box display="flex" flexDirection="column">
                <Typography variant="h5">
                    {i18n.t("caseOverview")} - {name}
                </Typography>
                <Typography variant="subtitle2" color="#7C828D">
                    {startDate} - {endDate}
                </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
                {exporting && <CircularProgress size={24} />}
                <Button
                    variant="contained"
                    onClick={() => setRegistryDialogOpen(true)}
                    disabled={exporting}
                    sx={{ borderRadius: 5 }}
                >
                    {i18n.t("registryExport.button")}
                </Button>
                <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
                <Button
                    variant="contained"
                    onClick={onNavigateToCategoriesOverview}
                    sx={{ borderRadius: 5 }}
                >
                    {i18n.t("CategoryList")}
                </Button>
                <Button
                    variant="contained"
                    onClick={onNavigateToVerifications}
                    sx={{ borderRadius: 5 }}
                >
                    {i18n.t("verificationList")}
                </Button>
                <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
                {downloading && <CircularProgress size={24} />}
                <Button
                    disabled={downloading}
                    variant="contained"
                    onClick={() => onDownload()}
                    sx={{ borderRadius: 5 }}
                >
                    {i18n.t("button.downloadVerification")}
                </Button>
                <Button
                    disabled={downloading}
                    variant="contained"
                    onClick={() => onDownload({ pdf: true })}
                    sx={{ borderRadius: 5 }}
                >
                    {i18n.t("button.downloadVerificationPDF")}
                </Button>
            </Box>
            <RegistryExportDialog
                open={registryDialogOpen}
                onClose={() => setRegistryDialogOpen(false)}
                attachments={attachments
                    .filter(a => !isReportPdf(a))
                    .filter((a, index, arr) => arr.findIndex(x => x.md5 === a.md5) === index)}
                hasSectionMap={hasSectionMap}
                onDownload={downloadRegistryExport}
                downloading={exporting}
            />
        </Box>
    );
};

export default Header;
